import { fabric } from 'fabric';
import type { EditorPlugin } from 'modules/fabric-editor';
import type { FabricEditor } from '../core/fabric-editor';
import { FabricPluginName } from './plugin-name';

const CLIPBOARD_PROPS = ['data'];

export type HotkeyHandler = (e: KeyboardEvent) => void;

export interface HotkeyFacade {
  bind(combo: string, handler: HotkeyHandler): void;
  unbind(combo: string): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    hotkeys?: HotkeyFacade;
  }
}

export interface HotkeyPluginOptions {
  enableDefaults?: boolean;
}

export function hotkeyPlugin(options: HotkeyPluginOptions = {}): EditorPlugin {
  const enableDefaults = options.enableDefaults ?? true;

  return {
    name: FabricPluginName.HOTKEYS,
    install(editor: FabricEditor) {
      const map = new Map<string, HotkeyHandler>();

      const onKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
        }

        const active = editor.canvas.getActiveObject() as (fabric.Object & { isEditing?: boolean }) | null;
        if (active?.isEditing) return;

        const handler = map.get(comboFromEvent(e));
        if (!handler) return;

        handler(e);
        e.preventDefault();
        e.stopPropagation();
      };

      const wrapper = (editor.canvas as fabric.Canvas & { wrapperEl?: HTMLElement }).wrapperEl;
      const prevOutline = wrapper?.style.outline ?? '';
      if (wrapper) {
        if (wrapper.tabIndex < 0) wrapper.tabIndex = 0;
        // tabIndex=0 makes the wrapper focusable so we can capture keydown; suppress the browser's default focus ring.
        wrapper.style.outline = 'none';
        wrapper.addEventListener('keydown', onKeyDown);
      }

      const facade: HotkeyFacade = {
        bind: (combo, handler) => map.set(normalize(combo), handler),
        unbind: (combo) => map.delete(normalize(combo)),
      };
      editor.hotkeys = facade;

      if (enableDefaults) {
        const undo = () => editor.history?.undo();
        const redo = () => editor.history?.redo();
        const deleteSelected = () => {
          const selected = editor.selection?.getActive() ?? [];
          if (selected.length === 0) return;
          for (const obj of selected) {
            editor.objects.remove(obj);
          }
          editor.canvas.discardActiveObject();
          editor.canvas.requestRenderAll();
        };

        facade.bind('ctrl+z', undo);
        facade.bind('meta+z', undo);
        facade.bind('ctrl+shift+z', redo);
        facade.bind('meta+shift+z', redo);
        facade.bind('ctrl+y', redo);
        facade.bind('meta+y', redo);
        facade.bind('delete', deleteSelected);
        facade.bind('backspace', deleteSelected);

        let clipboard: fabric.Object | null = null;

        const copy = () => {
          const active = editor.canvas.getActiveObject();
          if (!active) return;
          active.clone((cloned: fabric.Object) => {
            clipboard = cloned;
          }, CLIPBOARD_PROPS);
        };

        // Placement (offset + ActiveSelection expansion + select) lives in the
        // shared objects façade so Ctrl+V and the toolbar Duplicate button
        // behave identically. copy() still snapshots into `clipboard` so a copy
        // can be pasted repeatedly; duplicate() re-clones that snapshot.
        const paste = () => {
          if (!clipboard) return;
          editor.objects.duplicate(clipboard);
        };

        facade.bind('ctrl+c', copy);
        facade.bind('meta+c', copy);
        facade.bind('ctrl+v', paste);
        facade.bind('meta+v', paste);
      }

      (facade as HotkeyFacade & { __off?: () => void }).__off = () => {
        if (wrapper) {
          wrapper.removeEventListener('keydown', onKeyDown);
          wrapper.style.outline = prevOutline;
        }
        map.clear();
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.hotkeys as (HotkeyFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.hotkeys = undefined;
    },
  };
}

function normalize(combo: string): string {
  const parts = combo
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  const mods = { ctrl: false, shift: false, alt: false, meta: false };
  let key = '';
  for (const part of parts) {
    if (part === 'ctrl' || part === 'control') mods.ctrl = true;
    else if (part === 'shift') mods.shift = true;
    else if (part === 'alt' || part === 'option') mods.alt = true;
    else if (part === 'meta' || part === 'cmd' || part === 'command') mods.meta = true;
    else key = part;
  }
  return buildCombo(mods, key);
}

function comboFromEvent(e: KeyboardEvent): string {
  const mods = { ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey };
  return buildCombo(mods, keyTokenFromEvent(e));
}

function keyTokenFromEvent(e: KeyboardEvent): string {
  // Use physical key for letters/digits so non-Latin layouts (e.g. Cyrillic) don't break combos.
  const code = e.code;
  if (code.startsWith('Key') && code.length === 4) return code.slice(3).toLowerCase();
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  return e.key.toLowerCase();
}

function buildCombo(mods: { ctrl: boolean; shift: boolean; alt: boolean; meta: boolean }, key: string): string {
  const out: string[] = [];
  if (mods.ctrl) out.push('ctrl');
  if (mods.shift) out.push('shift');
  if (mods.alt) out.push('alt');
  if (mods.meta) out.push('meta');
  out.push(key);
  return out.join('+');
}
